// Submission Lane Types

export type SubmissionLaneType = 'FHIR_PAS' | 'PORTAL' | 'FAX_PHONE';

export type SubmissionStatus = 'PENDING' | 'SUBMITTED' | 'CONFIRMED' | 'FAILED' | 'RETRYING';

export interface SubmissionLog {
  id: string;
  authorization_id: string;
  lane: SubmissionLaneType;
  executed_by?: string;
  executed_at: string;
  submission_reference_id?: string;
  payer_confirmation_number?: string;
  submission_details?: SubmissionDetails;
  proof_file_path?: string;
  proof_url?: string;
  status: SubmissionStatus;
  error_message?: string;
  raw_request_payload?: Record<string, unknown>;
  raw_response_payload?: Record<string, unknown>;
  submitted_at?: string;
  confirmed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SubmissionDetails {
  // Portal lane details
  portal_url?: string;
  portal_user?: string;
  checklist_items?: ChecklistItem[];
  
  // Fax/Phone lane details
  fax_number?: string;
  phone_number?: string;
  contact_name?: string;
  call_duration_minutes?: number;
  fax_pages?: number;
  fax_confirmation_code?: string;
  
  // PAS lane details
  pas_endpoint?: string;
  pas_request_id?: string;
  
  // Common
  notes?: string;
}

export interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
  required: boolean;
  completed_at?: string;
}

export interface PayerSubmissionConfig {
  id: string;
  payer_id?: string;
  plan_id?: string;
  
  // FHIR PAS config
  fhir_pas_enabled: boolean;
  fhir_pas_endpoint?: string;
  fhir_pas_client_id?: string;
  
  // Portal config
  portal_enabled: boolean;
  portal_url?: string;
  portal_required_fields?: PortalRequiredField[];
  portal_instructions?: string;
  
  // Fax/Phone config
  fax_phone_enabled: boolean;
  fax_number?: string;
  phone_number?: string;
  fax_cover_template?: string;
  
  preferred_lane: SubmissionLaneType;
  
  created_at: string;
  updated_at: string;
}

export interface PortalRequiredField {
  field_name: string;
  field_type: 'text' | 'date' | 'select' | 'checkbox';
  required: boolean;
  options?: string[];
}

// Lane labels and icons
export const LANE_LABELS: Record<SubmissionLaneType, string> = {
  FHIR_PAS: 'FHIR PAS (Electronic)',
  PORTAL: 'Payer Portal',
  FAX_PHONE: 'Fax / Phone',
};

export const LANE_DESCRIPTIONS: Record<SubmissionLaneType, string> = {
  FHIR_PAS: 'Standards-based electronic submission via Da Vinci PAS',
  PORTAL: 'Manual submission through payer web portal',
  FAX_PHONE: 'Traditional fax or phone submission',
};

export const STATUS_LABELS: Record<SubmissionStatus, string> = {
  PENDING: 'Pending',
  SUBMITTED: 'Submitted',
  CONFIRMED: 'Confirmed',
  FAILED: 'Failed',
  RETRYING: 'Retrying',
};

export const STATUS_COLORS: Record<SubmissionStatus, string> = {
  PENDING: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  SUBMITTED: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  CONFIRMED: 'bg-primary/10 text-primary border-primary/30',
  FAILED: 'bg-red-500/10 text-red-500 border-red-500/30',
  RETRYING: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
};

// Default portal checklist items
export const DEFAULT_PORTAL_CHECKLIST: ChecklistItem[] = [
  { id: 'login', label: 'Logged into payer portal', completed: false, required: true },
  { id: 'patient', label: 'Patient information verified', completed: false, required: true },
  { id: 'member_id', label: 'Member ID confirmed', completed: false, required: true },
  { id: 'cpt_codes', label: 'CPT codes entered', completed: false, required: true },
  { id: 'diagnosis', label: 'Diagnosis codes entered', completed: false, required: true },
  { id: 'documents', label: 'Supporting documents uploaded', completed: false, required: true },
  { id: 'clinical_info', label: 'Clinical information provided', completed: false, required: false },
  { id: 'submitted', label: 'Authorization request submitted', completed: false, required: true },
  { id: 'confirmation', label: 'Confirmation number received', completed: false, required: true },
];
